import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";

import VerifiedAddressSearch from "components/address/VerifiedAddressSearch";
import useStorefrontRegister from "../../shared/auth/useStorefrontRegister";
import EdsLogo from "../components/EdsLogo";
import { EDS, btnPrimary, resetButton } from "../theme";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    fontSize: "0.9375rem",
    bgcolor: "#fff",
    "& fieldset": { borderColor: EDS.lineMedium },
    "&:hover fieldset": { borderColor: EDS.subtle },
    "&.Mui-focused fieldset": { borderColor: EDS.ink, borderWidth: 1 },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.875rem",
    color: EDS.muted,
    "&.Mui-focused": { color: EDS.ink },
  },
};

const authCardSx = {
  width: "100%",
  maxWidth: 440,
  bgcolor: EDS.surface,
  borderRadius: "16px",
  boxShadow: "0 12px 40px rgba(15, 23, 42, 0.06)",
  px: { xs: 3, sm: 4 },
  py: { xs: 4, sm: 4.5 },
};

export default function RegisterPage() {
  const {
    name,
    setName,
    email,
    setEmail,
    password,
    setPassword,
    confirm,
    setConfirm,
    phone,
    setPhone,
    selectedAddressCandidate,
    selectedAddressPlaceId,
    registrationAddressLookup,
    selectAddressCandidate,
    onAddressTyping,
    loading,
    message,
    canSubmit,
    handleSubmit,
    signInPath,
  } = useStorefrontRegister();

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        bgcolor: EDS.canvas,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: { xs: 4, md: 6 },
        fontFamily: EDS.fontDisplay,
        WebkitFontSmoothing: "antialiased",
      }}
    >
      <Box sx={authCardSx}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Link to="/" style={{ textDecoration: "none", display: "inline-block" }}>
            <EdsLogo variant="signIn" centered />
          </Link>
        </Box>

        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            component="h1"
            sx={{
              m: 0,
              fontSize: "1.5rem",
              fontWeight: 500,
              letterSpacing: "-0.03em",
              color: EDS.ink,
            }}
          >
            Create account
          </Box>
          <Box
            component="p"
            sx={{
              mt: 0.75,
              mb: 0,
              fontSize: "0.9375rem",
              color: EDS.muted,
              fontWeight: 400,
              lineHeight: 1.6,
            }}
          >
            Set up your Elite Drive account to order and track parts.
          </Box>
        </Box>

        {message ? (
          <Box
            component="p"
            sx={{
              mb: 2,
              fontSize: "0.875rem",
              color: message.type === "error" ? "#B42318" : "#147A5C",
              fontWeight: 400,
              textAlign: "center",
            }}
          >
            {message.text}
          </Box>
        ) : null}

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            "& .MuiInputBase-input, & input, & textarea": {
              fontSize: "16px !important",
            },
          }}
        >
          <Box sx={{ display: "grid", gap: 2 }}>
            <TextField
              label="Full name"
              fullWidth
              autoComplete="name"
              value={name}
              disabled={loading}
              onChange={(e) => setName(e.target.value)}
              sx={fieldSx}
            />
            <TextField
              type="email"
              label="Email"
              fullWidth
              autoComplete="email"
              value={email}
              disabled={loading}
              onChange={(e) => setEmail(e.target.value)}
              sx={fieldSx}
            />
            <TextField
              type="tel"
              label="Phone"
              fullWidth
              autoComplete="tel"
              value={phone}
              disabled={loading}
              onChange={(e) => setPhone(e.target.value)}
              sx={fieldSx}
            />
            <TextField
              type="password"
              label="Password"
              fullWidth
              autoComplete="new-password"
              value={password}
              disabled={loading}
              onChange={(e) => setPassword(e.target.value)}
              sx={fieldSx}
            />
            <TextField
              type="password"
              label="Confirm password"
              fullWidth
              autoComplete="new-password"
              value={confirm}
              disabled={loading}
              onChange={(e) => setConfirm(e.target.value)}
              sx={fieldSx}
              error={!!confirm && password !== confirm}
              helperText={
                confirm && password !== confirm ? "Passwords do not match." : " "
              }
              FormHelperTextProps={{ sx: { mx: 0, minHeight: 20 } }}
            />
            <VerifiedAddressSearch
              rowKey="registrationAddress"
              address={selectedAddressCandidate || {}}
              lookup={registrationAddressLookup}
              label="Shipping address"
              disabled={loading}
              helperText={
                selectedAddressPlaceId
                  ? "Address verified"
                  : "Street, city, state, postal code, and country."
              }
              onTyping={onAddressTyping}
              onSelect={(option) => selectAddressCandidate(option)}
            />
          </Box>

          <Box
            component="button"
            type="submit"
            disabled={loading || !canSubmit}
            sx={{
              ...btnPrimary,
              width: "100%",
              mt: 2.5,
              py: 1.25,
              opacity: loading || !canSubmit ? 0.55 : 1,
              cursor: loading || !canSubmit ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Creating account…" : "Create account"}
          </Box>
        </Box>

        <Box
          component="p"
          sx={{
            mt: 3,
            mb: 0,
            fontSize: "0.875rem",
            color: EDS.muted,
            fontWeight: 400,
            textAlign: "center",
          }}
        >
          Already have an account?{" "}
          <Link
            to={signInPath}
            style={{
              color: EDS.link,
              fontWeight: 500,
              textDecoration: "none",
            }}
          >
            Sign in
          </Link>
        </Box>
      </Box>
    </Box>
  );
}
